'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

// shadcn/ui
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

import {
  LayoutDashboard,
  CalendarDays,
  Calendar,
  Clock,
  Settings,
  Users,
  Building2,
  Contact,
  Ticket,
  ClipboardList,
  Menu,
  LogOut,
  LayoutList,
  Images,
  RefreshCw,
  Crown,
  Shield,
  Activity,
  BarChart3,
  Bell,
  ShieldCheck,
  TrendingDown,
  Undo2,
  CreditCard,
  Tag,
  Gift,
  ExternalLink,
  MessageCircle,
  PanelLeftClose,
  PanelLeftOpen,
  Eye,
  type LucideIcon,
} from 'lucide-react';

import { useRBAC } from '@/lib/auth/rbac';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { SidebarTrialPill } from '@/app/admin/_components/SidebarTrialPill';

/* ===================== Types ===================== */

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Optional red dot in the top-right corner — used for "you need
   *  to check Settings" signals like an outstanding calendar-sync
   *  alert. Keep it semantic: only set when there's a real owner-
   *  actionable problem, not for generic activity. */
  alertDot?: boolean;
}

interface NavSection {
  /** Optional overline shown above the group. Omit for ungrouped items. */
  heading?: string;
  items: NavItem[];
}

interface SidebarProps {
  isMobile?: boolean;
  onMobileClose?: () => void;
}

/* ===================== Hooks ===================== */

interface UserInfo {
  name: string;
  email: string;
  initial: string;
}

/**
 * Polls the calendar-sync summary endpoint and returns a boolean
 * "owner-actionable alert present" signal.
 *
 * Threshold rationale: 1+ dead_letter (connection broken — fix NOW)
 * OR 3+ pending (likely a prolonged outage). A single pending entry
 * is normal background noise — don't trigger the dot for it.
 */
// Module-level in-flight dedup so React 19 strict-mode double-mount
// (and parallel sidebar renders during admin nav) collapses to one
// network call. The poll itself runs every 60s so we only need this
// for the spike at mount time.
let inflightSyncSummary: Promise<Response> | null = null;
function dedupedSyncSummaryFetch(): Promise<Response> {
  if (inflightSyncSummary) return inflightSyncSummary.then((r) => r.clone());
  const p = fetch('/api/admin/calendar-sync-status/summary', { cache: 'no-store' }).finally(() => {
    inflightSyncSummary = null;
  });
  inflightSyncSummary = p;
  return p.then((r) => r.clone());
}

function useCalendarSyncAlert(): boolean {
  const [alert, setAlert] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      // Don't burn function CPU polling from a backgrounded tab. This is the
      // dominant Fluid-Active-CPU driver: the sidebar mounts on every admin
      // page and used to poll around the clock for every open/idle tab.
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await dedupedSyncSummaryFetch();
        if (!res.ok) return;
        const data = (await res.json()) as {
          pending: number;
          dead_letter: number;
        };
        if (cancelled) return;
        setAlert(data.dead_letter > 0 || data.pending >= 3);
      } catch {
        // Silent — sidebar shouldn't crash if the endpoint hiccups.
      }
    }
    poll();
    // 5min (was 60s): this only drives an alert dot for a BROKEN calendar sync
    // — minute-level freshness isn't needed, and 60s × every-open-tab × 24/7
    // was blowing the Hobby Fluid-Active-CPU cap.
    const t = setInterval(poll, 5 * 60_000);
    // Refresh promptly when the user returns to the tab.
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return alert;
}

function useAdminUser() {
  // RBAC now exposes role + loading; userId is no longer surfaced
  // through the shim (the resolver doesn't include it). Grab it
  // directly from the Supabase session below — same network cost
  // since we'd be calling getSession() anyway for the display-name.
  const {
    userRole: rbacRole,
    tenantId: rbacTenantId,
    loading: rbacLoading,
  } = useRBAC();
  // tenantName + storefront link come from the canonical resolver — no
  // separate query. `slug` is used for the "Vorschau Buchungsseite"
  // CTA so the owner can open `/${slug}` in a new tab without having
  // to remember the path.
  const { name: activeTenantName, slug: activeTenantSlug, primaryDomain } = useActiveTenant();

  const [user, setUser] = useState<UserInfo | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [tenantRole, setTenantRole] = useState<'tenant_owner' | 'employee' | null>(null);

  // Keep local tenantName in sync with the resolver's answer so the
  // sidebar updates immediately on tenant-switch.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional external-store sync (tenant switch)
    setTenantName(activeTenantName);
  }, [activeTenantName]);

  useEffect(() => {
    if (rbacLoading) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session.session) return;
        const userId = session.session.user.id;

        // Display-name (users) + tenant-role (employee_profiles) are
        // independent → fetch in parallel instead of two serial DB hops.
        const [userRes, employeeRes] = await Promise.all([
          supabase
            .from('users')
            .select('first_name, last_name')
            .eq('id', userId)
            .single(),
          rbacTenantId
            ? supabase
                .from('employee_profiles')
                .select('role')
                .eq('user_id', userId)
                .eq('tenant_id', rbacTenantId)
                .eq('is_active', true)
                .is('deleted_at', null)
                .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        ]);

        const userData = userRes.data;
        const employeeData = employeeRes.data as { role?: string } | null;
        if (!cancelled && employeeData?.role) {
          setTenantRole(employeeData.role as 'tenant_owner' | 'employee');
        }

        let name = 'Admin';
        if (userData?.first_name && userData?.last_name) {
          name = `${userData.first_name} ${userData.last_name}`;
        } else if (
          session.session.user.user_metadata?.first_name &&
          session.session.user.user_metadata?.last_name
        ) {
          name = `${session.session.user.user_metadata.first_name} ${session.session.user.user_metadata.last_name}`;
        } else if (session.session.user.user_metadata?.name) {
          name = session.session.user.user_metadata.name;
        }

        if (!cancelled) {
          setUser({
            name,
            email: session.session.user.email || '',
            initial: name.charAt(0).toUpperCase() || 'A',
          });
        }
      } catch {
        // graceful failure
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rbacTenantId, rbacLoading]);

  return {
    user,
    tenantName,
    tenantSlug: activeTenantSlug,
    primaryDomain,
    tenantRole,
    rbacRole,
    rbacLoading,
  };
}

/* ===================== Sidebar Content ===================== */

function SidebarContent({
  onMobileClose,
  collapsed = false,
  onToggleCollapse,
}: {
  onMobileClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const pathname = usePathname();
  const tNav = useTranslations('admin.navigation');
  const tSidebar = useTranslations('admin.sidebar');
  const tRoles = useTranslations('admin.roles');

  const { hasAnyPermission } = useRBAC();
  // Impersonation signal: the resolver sets role 'super_admin_view_as' ONLY
  // when a platform super-admin views a tenant they're not a member of (not for
  // a legit multi-tenant owner switching). Surfaces the view-as banner below.
  const { role: activeRole, name: activeTenantName } = useActiveTenant();
  const impersonating = activeRole === 'super_admin_view_as';
  const {
    user,
    tenantName,
    tenantSlug,
    primaryDomain,
    tenantRole,
    rbacRole,
    rbacLoading,
  } = useAdminUser();

  // "Vorschau Buchungsseite" target — prefer the tenant's verified
  // custom domain (cleaner share + better SEO trust) and fall back to
  // the slug-based public URL on eazybooking.de. If neither is known
  // yet (cold start), we leave the CTA hidden instead of linking to a
  // broken path.
  const storefrontHref = primaryDomain
    ? `https://${primaryDomain}`
    : tenantSlug
      ? `/${tenantSlug}`
      : null;

  const handleLogout = async () => {
    localStorage.clear();
    sessionStorage.clear();
    // The `tenant_id` cookie is HttpOnly (set server-side by the
    // active-tenant resolver), so JS cannot clear it directly. We
    // call a dedicated endpoint that issues `Set-Cookie: tenant_id=;
    // Max-Age=0` to expire it. Without this the next user logging
    // in on the same browser would inherit the previous tenant.
    try {
      await fetch('/api/auth/clear-tenant', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* non-fatal — signOut still proceeds, server cookie expires in 30d worst case */
    }
    await supabase.auth.signOut();
    window.location.href = '/admin/login';
  };

  const getRoleLabel = (role?: string | null) => {
    switch (role) {
      case 'super_admin':
        return tRoles('superAdmin');
      case 'admin':
        return tRoles('admin');
      case 'hospitality_manager':
        return tRoles('hospitalityManager');
      case 'property_manager':
        return tRoles('propertyManager');
      case 'calendar_manager':
        return tRoles('calendarManager');
      case 'booking_manager':
        return tRoles('bookingManager');
      case 'analytics_viewer':
        return tRoles('analyticsViewer');
      case 'business_owner':
        return tRoles('businessOwner');
      default:
        return tRoles('guest');
    }
  };

  // Calendar-sync alert state — poll the summary endpoint every 60s.
  // Set the dot when the owner has *actionable* problems waiting in
  // their settings: a `dead_letter` entry (connection broken — fix
  // NOW), or ≥3 `pending` entries (provider outage prolonged enough
  // to surface). Less than that = transient, leave the owner alone.
  const hasSyncAlert = useCalendarSyncAlert();

  /* ----- Build nav grouped by section ----- */

  const sections: NavSection[] = [];

  // Always visible dashboard
  sections.push({
    items: [
      { label: tNav('dashboard'), href: '/admin', icon: LayoutDashboard },
      // Benachrichtigungs-Center — für alle Tenant-Rollen sichtbar.
      ...(hasAnyPermission(['notifications.view'])
        ? [{ label: tNav('notifications'), href: '/admin/notifications', icon: Bell }]
        : []),
    ],
  });

  // Operations: bookings + services + hours + calendars
  const operations: NavItem[] = [];
  // Owner-Reporting (Umsatz/Auslastung/Conversion) — eigenes Gate reporting.view
  // (Umsatz = sensibel; nicht an booking.view gekoppelt).
  if (hasAnyPermission(['reporting.view'])) {
    operations.push({ label: tNav('reporting'), href: '/admin/reporting', icon: BarChart3 });
  }
  if (hasAnyPermission(['booking.view'])) {
    operations.push(
      { label: tNav('bookings'), href: '/admin/bookings', icon: CalendarDays },
      { label: tNav('services'), href: '/admin/services', icon: LayoutList },
      { label: tNav('hours'), href: '/admin/hours', icon: Clock }
    );
  }
  // Kundenkartei (Mini-CRM) — aus Buchungen aggregiert.
  if (hasAnyPermission(['customer.view'])) {
    operations.push({ label: tNav('customers'), href: '/admin/customers', icon: Contact });
    // Mehrfachkarten/Pakete — Kunden-Inventar (gleiche Berechtigung wie CRM).
    operations.push({ label: tNav('passes'), href: '/admin/passes', icon: Ticket });
    // Gutscheine / Gift Cards — Umsatz + Bindung (gleiche Berechtigung wie CRM).
    operations.push({ label: tNav('giftCards'), href: '/admin/gift-cards', icon: Gift });
  }
  // Intake-/Anamnese-Formulare — eigenes Gate: Gesundheitsdaten (Art. 9) sind
  // Need-to-Know, daher NICHT an customer.view gekoppelt.
  if (hasAnyPermission(['intake.view'])) {
    operations.push({ label: tNav('intake'), href: '/admin/intake', icon: ClipboardList });
  }
  // Galerie — Storefront-Content, Owner-only (settings.view).
  if (hasAnyPermission(['settings.view']) && tenantRole !== 'employee') {
    operations.push({ label: tNav('gallery'), href: '/admin/gallery', icon: Images });
  }
  if (hasAnyPermission(['calendar.view'])) {
    operations.push({
      label: tNav('bookingsCalendar'),
      href: '/admin/calendar/bookings-calendar',
      icon: Calendar,
    });
  }
  if (hasAnyPermission(['calendar_sync.view']) && tenantRole !== 'employee') {
    operations.push({
      label: tNav('calendarSync'),
      href: '/admin/calendar-sync',
      icon: RefreshCw,
    });
  }
  if (operations.length > 0) {
    sections.push({ heading: tSidebar('groupOperations'), items: operations });
  }

  // People: tenants + users + permissions + employees
  const people: NavItem[] = [];
  if (hasAnyPermission(['tenants.view'])) {
    people.push({ label: tNav('tenants'), href: '/admin/tenants', icon: Building2 });
  }
  if (hasAnyPermission(['users.view'])) {
    people.push({ label: tNav('users'), href: '/admin/users', icon: Users });
  }
  if (hasAnyPermission(['users.manage_roles', 'system.admin'])) {
    people.push({
      label: tNav('permissions'),
      href: '/admin/permissions',
      icon: Shield,
    });
  }
  // Note: `employees`, `subscription` and `domains` previously lived here;
  // they now live as sections inside /admin/settings so the main nav stays
  // focused on day-to-day workflows (bookings, calendar) and the settings
  // sidebar owns configuration. `packages` stays — it's a super-admin tool
  // for plan management, not customer-facing billing.
  if (people.length > 0) {
    sections.push({ heading: tSidebar('groupPeople'), items: people });
  }

  // Billing — only super-admin tool (packages); customer-facing /admin/subscription
  // is reachable via Settings → Abrechnung.
  const billing: NavItem[] = [];
  if (hasAnyPermission(['subscription_plans.view'])) {
    billing.push({ label: tNav('packages'), href: '/admin/packages', icon: Crown });
  }
  if (billing.length > 0) {
    sections.push({ heading: tSidebar('groupBilling'), items: billing });
  }

  // Configuration
  const config: NavItem[] = [];
  if (hasAnyPermission(['settings.view'])) {
    config.push({
      label: tNav('settings'),
      href: '/admin/settings',
      icon: Settings,
      // Alert-dot fires when there's an owner-actionable calendar-sync
      // problem (dead_letter present, or 3+ pending entries). Owner
      // sees the red dot at-a-glance without opening Settings.
      alertDot: hasSyncAlert,
    });
  }
  if (hasAnyPermission(['monitoring.view'])) {
    config.push({
      label: tNav('monitoring'),
      href: '/admin/monitoring',
      icon: Activity,
    });
  }
  if (hasAnyPermission(['billing.manage', 'system.admin'])) {
    config.push({
      label: tNav('avv'),
      href: '/admin/legal/avv',
      icon: ShieldCheck,
    });
  }
  if (config.length > 0) {
    sections.push({ heading: tSidebar('groupConfiguration'), items: config });
  }

  // Wachstum — tenant-owner-facing growth tools. Currently only the
  // referral programme; later additions (review-request automation,
  // share-template generator, customer-NPS dashboard) live here.
  const growth: NavItem[] = [];
  if (hasAnyPermission(['subscription.view', 'billing.view'])) {
    growth.push({
      label: tSidebar('referrals'),
      href: '/admin/referrals',
      icon: Gift,
    });
  }
  if (growth.length > 0) {
    sections.push({ heading: tSidebar('groupGrowth'), items: growth });
  }

  // Platform (super_admin only)
  // F-500 RBAC mapper collapses DB role 'super_admin' → enum 'admin'.
  // Check the enum here (was: 'super_admin' → never matched after
  // the unified-resolver refactor, hiding the whole PLATFORM section).
  if (rbacRole === 'admin') {
    sections.push({
      heading: tSidebar('groupPlatform'),
      items: [
        {
          label: tNav('systemHealth'),
          href: '/admin/system-health',
          icon: Activity,
        },
        {
          // Churn insights — backed by the Stripe Customer Portal
          // cancellation survey we capture via webhook. Falls under the
          // Platform group because the data is cross-tenant.
          label: 'Cancellations',
          href: '/admin/insights/cancellations',
          icon: TrendingDown,
        },
        {
          // Marketing promo-codes — Stripe Coupons + PromotionCodes
          // management. Same surface as winback codes, plus manual
          // campaign creation (BLACKFRIDAY30 etc.).
          label: 'Promo-Codes',
          href: '/admin/insights/promo-codes',
          icon: Tag,
        },
        {
          // Customer-payment funnel + revenue-per-currency from Stripe Connect.
          label: 'Payments',
          href: '/admin/insights/payments',
          icon: CreditCard,
        },
        {
          // Refund audit trail — every tenant-cancel + Stripe-side refund.
          label: 'Refunds',
          href: '/admin/insights/refunds',
          icon: Undo2,
        },
        {
          // Cross-tenant support inbox — Telegram-bridged conversations.
          label: 'Support',
          href: '/admin/insights/support',
          icon: MessageCircle,
        },
      ],
    });
  }

  const isActive = (href: string) => {
    if (href === '/admin') return pathname === '/admin';
    return pathname === href || pathname.startsWith(href + '/');
  };

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Brand row */}
      <div
        className={cn(
          'flex h-16 flex-shrink-0 items-center border-b border-gray-200/80',
          collapsed ? 'justify-center px-2' : 'px-5'
        )}
      >
        <Link
          href="/admin"
          className="flex items-center"
          aria-label="EazyBooking Admin"
        >
          {collapsed ? (
            <Image
              src="/logo.svg"
              alt="EazyBooking"
              width={32}
              height={32}
              className="h-8 w-8 object-contain"
            />
          ) : (
            <Image
              src="/logo.svg"
              alt="EazyBooking"
              width={140}
              height={32}
              className="h-8 w-auto"
            />
          )}
        </Link>
      </div>

      {/* Tenant switcher is rendered in the top-bar (AdminViewAsDropdown) —
          mounting it here too caused two parallel auth fetches and blocked
          reloads after tenant switch. Keep one source of truth. */}

      {/* User card */}
      <div className="border-b border-gray-200/80 px-3 pt-3">
        {rbacLoading ? (
          collapsed ? (
            <div className="flex justify-center px-3 py-2.5">
              <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-3 py-2.5">
              <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-full bg-gray-200" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-2.5 w-16 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          )
        ) : user ? (
          <div className={cn('py-2.5', collapsed ? 'px-0' : 'px-3')}>
            <div
              className={cn(
                'flex items-center rounded-xl',
                collapsed ? 'justify-center' : 'gap-3'
              )}
            >
              <div
                title={collapsed ? user.name : undefined}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white"
              >
                {user.initial}
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-950">
                    {user.name}
                  </p>
                  <div className="flex items-center gap-1 truncate text-xs text-gray-500">
                    {tenantName ? (
                      <>
                        <Building2 className="h-3 w-3 flex-shrink-0" strokeWidth={2} />
                        <span className="truncate">{tenantName}</span>
                      </>
                    ) : (
                      <span className="truncate">{getRoleLabel(rbacRole)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {!collapsed && <SidebarTrialPill onNavigate={onMobileClose} />}
            {!collapsed && storefrontHref && (
              // "Vorschau Buchungsseite": always-visible escape hatch into
              // the public storefront. F-500 onboarding research: owners
              // who never view their own page tend to ship typos / broken
              // service descriptions to customers. This one tap lets them
              // proof-read end-to-end without leaving the admin shell.
              <a
                href={storefrontHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onMobileClose}
                className="mt-2 flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
                Buchungsseite ansehen
              </a>
            )}
          </div>
        ) : null}
      </div>

      {/* View-as banner — only for true super-admin impersonation of a
          non-member tenant (role 'super_admin_view_as'). Mirrors the topbar
          AdminViewAsDropdown so the operator can't forget they're acting on a
          customer tenant. */}
      {impersonating &&
        (collapsed ? (
          <div className="mt-3 flex justify-center" title={`Angesehen als ${activeTenantName ?? 'Tenant'}`}>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-amber-200 bg-amber-50">
              <Eye className="h-4 w-4 text-amber-600" strokeWidth={2.25} />
            </span>
          </div>
        ) : (
          <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <Eye className="h-4 w-4 flex-shrink-0 text-amber-600" strokeWidth={2.25} />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                Angesehen als
              </span>
              <span className="block truncate text-sm font-medium text-amber-900">
                {activeTenantName ?? 'Tenant'}
              </span>
            </span>
          </div>
        ))}

      {/* Navigation */}
      <nav
        className="flex-1 space-y-5 overflow-y-auto px-3 py-4"
        aria-label="Admin navigation"
      >
        {sections.map((section, sIdx) => (
          <div key={sIdx}>
            {!collapsed && section.heading && (
              <div className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                {section.heading}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(item.href);
                const Icon = item.icon;
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={onMobileClose}
                      aria-current={active ? 'page' : undefined}
                      title={collapsed ? item.label : undefined}
                      className={cn(
                        'group relative flex items-center rounded-lg py-2 text-sm font-medium transition-colors duration-100',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
                        collapsed ? 'justify-center px-2' : 'gap-3 px-3',
                        active
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-950'
                      )}
                    >
                      <span className="relative flex-shrink-0">
                        <Icon
                          className={cn(
                            'h-[18px] w-[18px] transition-colors',
                            active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-700'
                          )}
                          strokeWidth={2}
                        />
                        {collapsed && item.alertDot && (
                          <span
                            aria-label="Achtung benötigt"
                            className="absolute -right-1 -top-1 inline-flex h-2 w-2 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]"
                          />
                        )}
                      </span>
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {!collapsed && item.alertDot && (
                        <span
                          aria-label="Achtung benötigt"
                          className="ml-auto inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]"
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 space-y-2 border-t border-gray-200/80 px-3 py-3">
        {!collapsed && (
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>
        )}

        {/* Collapse / expand toggle — desktop only (onToggleCollapse is
            undefined in the mobile Sheet, so the button stays hidden there). */}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
            title={collapsed ? 'Ausklappen' : 'Einklappen'}
            className={cn(
              'flex w-full items-center rounded-lg py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
              collapsed ? 'justify-center px-2' : 'gap-2 px-3'
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
            ) : (
              <>
                <PanelLeftClose className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
                <span>Einklappen</span>
              </>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={handleLogout}
          aria-label={collapsed ? tSidebar('logoutButton') : undefined}
          title={collapsed ? tSidebar('logoutButton') : undefined}
          className={cn(
            'flex w-full items-center rounded-lg py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-rose-50 hover:text-rose-700',
            collapsed ? 'justify-center px-2' : 'gap-2 px-3'
          )}
        >
          <LogOut className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
          {!collapsed && tSidebar('logoutButton')}
        </button>
      </div>
    </div>
  );
}

/* ===================== Exports ===================== */

export function AdminSidebar({ isMobile = false }: SidebarProps) {
  const tSidebar = useTranslations('admin.sidebar');
  const [open, setOpen] = useState(false);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label={tSidebar('menuToggle')}
          >
            <Menu className="h-5 w-5" strokeWidth={2} />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-72 p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{tSidebar('menuTitle')}</SheetTitle>
          </SheetHeader>
          <SidebarContent onMobileClose={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    );
  }

  return <DesktopSidebar />;
}

const COLLAPSE_STORAGE_KEY = 'admin-sidebar-collapsed';

function DesktopSidebar() {
  const [collapsed, setCollapsed] = useState(false);

  // Restore persisted state on mount (SSR-safe — localStorage is only
  // touched in the effect, so the server render is always expanded and
  // hydration stays deterministic).
  useEffect(() => {
    try {
      if (localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1') {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional
        setCollapsed(true);
      }
    } catch {
      /* localStorage unavailable (private mode) — stay expanded */
    }
  }, []);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* non-fatal — just won't persist */
      }
      return next;
    });
  };

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-screen flex-shrink-0 border-r border-gray-200/80 bg-white md:block',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[76px]' : 'w-72'
      )}
    >
      <SidebarContent collapsed={collapsed} onToggleCollapse={toggleCollapse} />
    </aside>
  );
}

export default AdminSidebar;
